import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { LL } from 'i18n/i18n.service';
import { CsPage, NavController } from 'providers/nav-controller';
import { AuthService } from 'services/auth.service';
import { LoggingService } from 'services/logging.service';
import { describeError } from '@cf-shared/error-describe';
import { AlertController, AlertButtonRole } from 'providers/alert-controller';
import { PaddleCheckoutService } from 'services/paddle-checkout.service';
import { CoachBillingService } from 'services/coach-billing.service';
import { UrlQueryService } from 'services/url-query-service';
import { PaddleCheckoutConfig } from '@cf-shared/store-interface';
import { CoachStartPaddleCheckoutResult, GetCoachBillingStateResult } from '@cf-shared/coach-billing-interface';
import { PaddleEventData } from '@paddle/paddle-js';
import { TranslatedString } from 'models/translated-string';
import { getMyApp } from 'app/app-interface';
import { CoachCheckoutPageParams } from './coach-checkout-page-interface';
import { kCoachSeatWaitBudget, pollForPostPurchaseAsync } from 'pages/subscribe/post-purchase-wait-policy';
import {
  dispatchPaddleCheckoutEvent,
  kPaddleCheckoutFailedNoFieldsReport,
  kPaddleCheckoutNetworkErrorReport,
  kPaddleFrameClass,
  openWhenPaddleFrameReady,
  PaddleCheckoutErrorKind,
  PaddleCheckoutPage,
  paddleCheckoutErrorContext,
} from 'pages/subscribe/paddle-inline-frame';

/**
 * Become-a-coach checkout on the web (Paddle per-seat rail, Phase 4, #3919).
 *
 * Reached from the "Become a Coach" entry on the user-profile (Manage
 * Subscription) menu. It hosts the EMBEDDED inline Paddle checkout for the
 * coach-seat price at quantity 1 (the coach's own seat, seat #1 that grants the
 * coach personal Premium) — the same embedded-frame model the subscribe page
 * uses, deliberately NOT a Paddle pop-up overlay (all our checkouts are
 * embedded, so they look and behave like the subscribe page).
 *
 * Flow, mirroring the subscribe page's logged-in Paddle branch but with a single
 * product (no plan selector) and an always-logged-in buyer:
 *   1. Call the authoritative server gate (`startPaddleCheckoutAsync`). On a
 *      cross-provider conflict (a live personal sub on another provider) or any
 *      other error, alert and go back — the frame never opens.
 *   2. Init Paddle.js, then open the inline checkout for `priceCoachSeat` at
 *      quantity 1 into our `.paddle-checkout-frame` container.
 *   3. On `CHECKOUT_COMPLETED`, show a Continue button (the grant lands
 *      out-of-band via the coach-seat webhook). Continue polls the coach billing
 *      state until the seat grant appears, then reloads the app so it comes up
 *      recognizing the new coach.
 *
 * Client-side seat-quantity changes after this (add/remove client) do NOT reopen
 * this page — the server PATCHes the existing subscription's quantity behind the
 * scenes (Step 6). This page is only for the one-time initial purchase.
 */
@Component({
  selector: 'page-coach-checkout',
  templateUrl: 'coach-checkout-page.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CoachCheckoutPage extends CsPage<CoachCheckoutPageParams> {
  protected LL = LL;

  /** True while the server gate is in flight, before the frame can open. */
  loading = true;
  /** True while the Paddle frame is opening (or the grant is finalizing). */
  loadingCheckout = false;
  /** True once `CHECKOUT_COMPLETED` fired — reveals our Continue button. */
  purchaseComplete = false;

  private config?: PaddleCheckoutConfig;
  private finalizing = false;
  /** Guard so a re-entered page instance doesn't re-run the gate/checkout. */
  private started = false;

  constructor(
    public navCtrl: NavController,
    private auth: AuthService,
    private logging: LoggingService,
    private alertCtrl: AlertController,
    private paddleCheckout: PaddleCheckoutService,
    private coachBilling: CoachBillingService,
    private urlQuery: UrlQueryService,
    private cdr: ChangeDetectorRef,
  ) {
    super();
  }

  async ionViewDidEnter() {
    // run the gate + open the checkout exactly once per page instance.
    if (this.started) return;
    this.started = true;
    await this.beginAsync();
  }

  async ionViewWillLeave() {
    // tear down any open Paddle inline checkout so its eventCallback closure
    // does not fire on this now-stale page after navigation away.
    this.paddleCheckout.close();
    this.finalizing = false;
    this.purchaseComplete = false;
  }

  // gate → init Paddle → open the inline coach-seat checkout at quantity 1.
  private async beginAsync() {
    let result: CoachStartPaddleCheckoutResult;
    try {
      result = await this.coachBilling.startPaddleCheckoutAsync();
    } catch (err) {
      this.logging.trackAbnormalException(err, LL.common.errors.generic());
      await this.alertAndBackAsync(LL.coach.tier.becomeCoach(), LL.common.errors.generic());
      return;
    }
    if (this.viewDestroyed) return;

    if ('error' in result) {
      // the authoritative server gate refused. cross_provider_conflict gets the
      // dedicated single-provider copy; anything else is a generic failure that
      // must also reach telemetry.
      if (result.error === 'cross_provider_conflict') {
        await this.alertAndBackAsync(LL.coach.tier.becomeCoach(), LL.coach.tier.crossProviderConflict());
      } else {
        this.logging.trackAbnormalError(result.error, LL.common.errors.generic());
        await this.alertAndBackAsync(LL.coach.tier.becomeCoach(), LL.common.errors.generic());
      }
      return;
    }

    this.config = result;

    // a sandbox checkout that is NOT e2e-marked can never grant (the webhook
    // sandbox grant-gate refuses it), so don't render a checkout the user could
    // complete but never be granted from. This only happens off-prod (dev,
    // without the e2e-test marker). Mirrors subscribe's same guard.
    if (this.config.environment === 'sandbox' && !this.config.isE2e) {
      console.warn('Paddle: coach checkout not available for this request.');
      await this.alertAndBackAsync(LL.coach.tier.becomeCoach(), LL.common.errors.generic());
      return;
    }

    try {
      await this.paddleCheckout.initAsync({
        environment: this.config.environment,
        token: this.config.clientToken,
        eventCallback: (ev) => this.onPaddleEvent(ev),
      });
    } catch (err) {
      this.logging.trackAbnormalException(err, LL.common.errors.generic());
      await this.alertAndBackAsync(LL.coach.tier.becomeCoach(), LL.common.errors.generic());
      return;
    }
    if (this.viewDestroyed) return;

    // gate passed + Paddle ready — reveal the frame container and open into it.
    this.loading = false;
    this.loadingCheckout = true;
    this.cdr.markForCheck();
    // the inline frame mounts into a div Angular renders only once `loading` is
    // false, so wait for the container element to exist before opening.
    openWhenPaddleFrameReady({
      schedule: (callback, delayMs) => this.safeTimeout(callback, delayMs),
      shouldStop: () => this.viewDestroyed || !this.config,
      onFrameReady: () => this.openIntoFrame(),
      // clear the spinner and surface a recovery path so the coach is never left on
      // a dead spinner (the frame container should always render, so this is a
      // should-never-happen backstop — but it must not strand the user).
      onGiveUp: () => {
        this.logging.trackSilentProblem('CoachPaddleFrameTargetMissing', {});
        this.loadingCheckout = false;
        this.cdr.markForCheck();
        void this.alertAndBackAsync(LL.coach.tier.becomeCoach(), LL.common.errors.generic());
      },
    });
  }

  // the frame container exists: stamp custom_data and open the checkout into it.
  private openIntoFrame() {
    const customData: Record<string, unknown> = {
      origin_host: location.host,
      checkout_id: this.config.checkoutId,
      // same tracking channel the personal subscribe checkout uses (#4045,
      // #4257) — see PaddleCustomData.tracking. A coach-seat purchase sends no
      // Mixpanel event and fires no pixel (the webhook gates both on
      // !isCoachSeat), so what carrying this actually achieves here is the
      // updateUserTelemetryData write, which runs BEFORE that split: it records
      // the coach's acquisition channel on their user document, so their later
      // events carry it. Keeping both Paddle checkouts on one rule also means
      // neither can silently drift from the other.
      // the second argument is the Global Privacy Control signal this server saw
      // on the request that built this config (#5663). It matters here even
      // though a coach-seat purchase fires no pixel: recordCheckoutAdConsent
      // runs BEFORE the coach/personal split, so the answer this checkout
      // carries is stored on the account and a later personal trial-to-paid
      // report follows it.
      tracking: this.urlQuery.getTrackingDataForCheckout(
        this.config.clientIp, this.config.gpcDetected === true),
    };
    // stamp the coach uid (Phase 1 identity) — become-a-coach is always an
    // existing logged-in coach, so this is normally set.
    if (this.auth.user?.uid) customData.app_user_id = this.auth.user.uid;
    if (this.config.isE2e) customData.is_e2e = true;
    try {
      this.paddleCheckout.openInlineCheckout({
        priceId: this.config.priceCoachSeat,
        quantity: 1, // the coach's own seat (seat #1)
        // bare class name (not a selector) — Paddle mounts via getElementsByClassName.
        frameTarget: kPaddleFrameClass,
        customData,
        // a returning Paddle customer (e.g. an existing personal Paddle subscriber)
        // gets a streamlined checkout (#4097). The service picks one handle by
        // precedence: customerAuthToken (presents the saved address + saved card)
        // → customerId + addressId (attaches to their customer record + skips the
        // details page) → the locked account email for a first-time buyer. Any of
        // the returning-customer fields may be absent (e.g. no address captured
        // yet, or token minting failed); the service degrades gracefully.
        customerId: this.config.existingCustomerId,
        addressId: this.config.existingAddressId,
        customerAuthToken: this.config.customerAuthToken,
        customerEmail: this.auth.user?.email || undefined,
        // lock the email — become-a-coach is always an existing logged-in coach.
        allowLogout: false,
        // deliberately NO successUrl: for an inline checkout a successUrl makes
        // Paddle redirect the top window on completion, tearing this page down
        // before CHECKOUT_COMPLETED can run. We stay on the page and drive the
        // finalize ourselves on Continue.
      });
    } catch (err) {
      // Paddle.Checkout.open can throw synchronously (e.g. a bad frameTarget):
      // clear the spinner and surface so the user never stares at a frozen frame.
      this.loadingCheckout = false;
      this.cdr.markForCheck();
      this.logging.trackAbnormalException(err, LL.common.errors.generic());
    }
  }

  // report one Paddle checkout error at the tier its kind deserves (df-38f32a018053).
  // it runs even after the page is gone, so it must not touch the view.
  private reportPaddleCheckoutError(errorEvent: PaddleEventData, detail: string, kind: PaddleCheckoutErrorKind): void {
    console.error(`Paddle ${errorEvent.name}: type=${errorEvent.type} code=${errorEvent.code} detail=${errorEvent.detail}`);
    switch (kind) {
      case PaddleCheckoutErrorKind.Network:
        // the request never reached Paddle (offline, ad blocker): not our fault, and
        // Paddle's frame already shows its own error, so no toast (df-38f32a018053)
        this.logging.trackSilentInfo(kPaddleCheckoutNetworkErrorReport, paddleCheckoutErrorContext(errorEvent, PaddleCheckoutPage.CoachCheckout));
        break;
      case PaddleCheckoutErrorKind.NoFields:
        // cause unknown: keep it a problem, and record how this session's last price
        // preview ended (this page asks for none, so it is usually "not-asked")
        this.logging.trackSilentProblem(kPaddleCheckoutFailedNoFieldsReport, {
          page: PaddleCheckoutPage.CoachCheckout,
          event: errorEvent.name,
          lastPricePreview: this.paddleCheckout.lastPricePreview,
        });
        break;
      default:
        this.logging.trackAbnormalException(
          new Error(`Coach Paddle checkout ${errorEvent.name}: ${detail}`),
          LL.common.errors.generic(),
        );
        break;
    }
  }

  // Paddle.js event callback. The ordering rule it shares with the subscribe rail —
  // an error is reported even on a torn-down page (Paddle keeps the callback closure
  // alive across navigation), every other event is dropped there — lives in
  // dispatchPaddleCheckoutEvent.
  private onPaddleEvent(ev: PaddleEventData) {
    dispatchPaddleCheckoutEvent(ev, {
      isViewGone: () => this.viewDestroyed,
      reportCheckoutError: (errorEvent, detail, kind) => this.reportPaddleCheckoutError(errorEvent, detail, kind),
      stopSpinner: () => {
        this.loadingCheckout = false;
        this.cdr.markForCheck();
      },
      // email guard (see guardCustomerEmailAsync): for a first-time buyer the
      // email is locked so a mismatch means a real fault (block + log); for a
      // returning customer opened by customer.id a differing stored email is
      // benign drift (log only, never block).
      onCustomerEmail: (email) => {
        void this.guardCustomerEmailAsync(email);
      },
      onCompleted: () => {
        // payment succeeded (Paddle's client-side signal). the coach-seat grant
        // still arrives out-of-band via the webhook a few seconds later. Paddle
        // now shows its success screen in the frame; surface our Continue button
        // beside it and do NOT reload yet.
        console.log('Coach Paddle: checkout completed');
        this.purchaseComplete = true;
        this.cdr.markForCheck();
      },
    });
  }

  // guard against a coach-seat sub being created under the wrong identity. How
  // strict to be depends on HOW the checkout was opened:
  //  - first-time buyer (opened with customer.email + allowLogout:false): the
  //    email is the identity and it is locked, so a differing email should be
  //    impossible — treat a mismatch as a real fault: log it and tear the
  //    checkout down.
  //  - returning customer (opened with customer.id): the id — resolved from THIS
  //    coach's own paddleData — is the authoritative billing tie, so the sub
  //    attaches to the right Paddle customer regardless of email. The customer's
  //    STORED Paddle email can legitimately differ from their current app-account
  //    email (they changed one and not the other), so a difference here is benign
  //    drift, NOT a mis-tie: log it for observability but do NOT block the coach.
  private async guardCustomerEmailAsync(email?: string) {
    if (!email) return;
    const accountEmail = this.auth.user?.email;
    // if the account email is unknown we cannot compare — do not tear down a
    // valid checkout on a comparison we can't make. (expected only in a
    // never-logged-in case, which become-a-coach does not reach.)
    if (!accountEmail) return;
    // the emails match — nothing to guard
    if (email.toLowerCase() === accountEmail.toLowerCase()) return;
    // emails differ — opened by the returning customer's Paddle id: the id is the
    // authoritative tie, so this is benign stored-email drift. record it (no raw
    // emails — PII) and let the coach continue.
    if (this.config.existingCustomerId) {
      this.logging.trackSilentProblem('CoachPaddleCustomerEmailDiffersFromAccount', { openedBy: 'customerId' });
      return;
    }
    // emails differ — opened by the locked account email: a mismatch here should
    // be impossible, so surface it (telemetry so a field occurrence is visible)
    // and tear the checkout down rather than tie the sub to a different email.
    this.logging.trackSilentProblem('CoachPaddleCustomerEmailMismatch', { openedBy: 'accountEmail' });
    this.paddleCheckout.close();
    await this.alertAndBackAsync(LL.coach.tier.becomeCoach(), LL.common.errors.generic());
  }

  // Continue tapped on Paddle's success screen: poll the coach billing state
  // until the seat grant has landed (coachSeatData → seatState present), then
  // reload the app so it comes up recognizing the new coach. Mirrors subscribe's
  // onContinueAfterPaddlePurchase, but keyed on the coach-seat grant rather than
  // personal-subscription Active (a coach may already have been Paid via a key,
  // so "Active" alone would not prove the NEW coach grant landed).
  async onContinueAsync() {
    if (this.finalizing) return;
    this.finalizing = true;
    this.loadingCheckout = true;
    this.cdr.markForCheck();

    // how long this waits, and why it matches the subscribe page's already-signed-in
    // wait, lives in post-purchase-wait-policy.ts (#4488).
    // remember the last poll error so a PERSISTENT failure across every attempt
    // can surface once at timeout (below). We do NOT log per attempt: the
    // coach-gated state read is refused with a 403 until the grant lands, which
    // is the normal in-flight race, so per-attempt logging would spam telemetry
    // per purchase.
    let lastError: unknown;
    const granted = await pollForPostPurchaseAsync<GetCoachBillingStateResult>(kCoachSeatWaitBudget, {
      isCancelled: () => this.viewDestroyed,
      attemptAsync: async () => {
        try {
          const state = await this.coachBilling.loadStateAsync();
          // no seat state yet means the grant has not landed — keep waiting
          return state.seatState ? state : undefined;
        } catch (err) {
          // expected while the webhook is still in flight (the coach-gated state
          // read answers 403 until the grant lands) — keep polling; stash the
          // error so a persistent failure can surface at timeout.
          lastError = err;
          return undefined;
        }
      },
    });
    if (granted.cancelled || this.viewDestroyed) return;

    if (!granted.result) {
      // the grant did not arrive in time. a persistent failure across every
      // attempt is the genuinely unexpected case (vs a merely-slow webhook), so
      // surface it once to telemetry — mirroring subscribe's
      // onContinueAfterPaddlePurchase — before showing the recovery alert.
      this.logging.trackSilentProblem('CoachPaddleFinalizeTimeout', {
        error: lastError === undefined ? undefined : describeError(lastError),
      });
      // surface the existing "registration slow" alert (its Retry reloads) so the
      // user is not left staring at the checkout, then reload so they can recover.
      this.loadingCheckout = false;
      this.finalizing = false;
      this.cdr.markForCheck();
      await this.alertCtrl.showAlertAsync({
        title: LL.subscribe.alerts.registrationSlow.title(),
        message: LL.subscribe.alerts.registrationSlow.message(),
        buttons: [{ text: LL.fatalError.buttons.retry(), role: AlertButtonRole.Primary, result: null }],
      });
      if (this.viewDestroyed) return;
    }
    // grant landed (or the user acknowledged the slow-registration notice) — a
    // full reload brings the app up cleanly recognizing the new coach state.
    void getMyApp().reloadAppAsync({ resetToRoot: true });
  }

  // shared: surface a translated alert, then pop back to the previous page.
  private async alertAndBackAsync(title: TranslatedString, message: TranslatedString) {
    if (this.viewDestroyed) return;
    this.loading = false;
    this.cdr.markForCheck();
    await this.alertCtrl.showAlertAsync({
      title,
      message,
      buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary, result: null }],
    });
    if (this.viewDestroyed) return;
    await this.navCtrl.popAsync();
  }
}
